'use client'

import React, { useState, useEffect } from 'react'
import { ShoppingCart as ShoppingCartIcon } from 'lucide-react'
import '../../../sass/menu/OrderingWidget.scss'
import { useCart } from '@/contexts/CartContext'
import { Currency, Language } from '@/lib/interfaces/interfaces'
import { IOrderPayload, IShippingDetails } from '@/lib/interfaces/ordering'
import { formatMoney } from '@/lib/utils/format'
import axios from 'axios'
import CartList from './CartList'
import ActionButtons from './ActionButtons'
import ShippingDetails from './ShippingDetails'
import DeliveryEmbeddedCheckout from './DeliveryEmbeddedCheckout'
import AppSnackbar from '@/components/ui/AppSnackbar'

interface Props {
  slug: string
  language: Language
  barId: string
  deliveryFee: number
  minOrder: number
  barCoords?: { lat: number; lng: number }
  currency: Currency
}

// Objeto de traducciones con textos para cada idioma
const translations = {
  es: {
    requiredFirstName: 'El nombre es requerido',
    requiredLastName: 'El apellido es requerido',
    requiredAddress: 'La dirección es requerida',
    requiredCity: 'La ciudad es requerida',
    requiredPostalCode: 'El código postal es requerido',
    requiredProvince: 'La provincia es requerida',
    invalidPhone: 'El teléfono no es correcto',
    product: 'producto',
    products: 'productos',
    openCart: 'Abrir carrito',
    minOrderError: 'El pedido mínimo es de',
    deliveryZoneError: 'Lo sentimos, no entregamos en tu zona',
    geocodingError: 'No pudimos verificar tu dirección. Revisa que sea correcta.',
    orderProcessError: 'Error al procesar el pedido'
  },
  en: {
    requiredFirstName: 'First name is required',
    requiredLastName: 'Last name is required',
    requiredAddress: 'Address is required',
    requiredCity: 'City is required',
    requiredPostalCode: 'Postal code is required',
    requiredProvince: 'Province is required',
    invalidPhone: 'Phone number is invalid',
    product: 'product',
    products: 'products',
    openCart: 'Open cart',
    minOrderError: 'Minimum order amount is',
    deliveryZoneError: 'Sorry, we do not deliver to your area',
    geocodingError: 'We could not verify your address. Please check it is correct.',
    orderProcessError: 'Error processing the order'
  },
  fr: {
    requiredFirstName: 'Le prénom est requis',
    requiredLastName: 'Le nom est requis',
    requiredAddress: "L'adresse est requise",
    requiredCity: 'La ville est requise',
    requiredPostalCode: 'Le code postal est requis',
    requiredProvince: 'La province est requise',
    invalidPhone: "Le téléphone n'est pas correct",
    product: 'produit',
    products: 'produits',
    openCart: 'Ouvrir le panier',
    minOrderError: 'Le montant minimum de commande est de',
    deliveryZoneError: 'Désolé, nous ne livrons pas dans votre zone',
    geocodingError: "Nous n'avons pas pu vérifier votre adresse. Veuillez la vérifier.",
    orderProcessError: 'Erreur lors du traitement de la commande'
  },
  ca: {
    requiredFirstName: 'El nom és obligatori',
    requiredLastName: 'El cognom és obligatori',
    requiredAddress: "L'adreça és obligatòria",
    requiredCity: 'La ciutat és obligatòria',
    requiredPostalCode: 'El codi postal és obligatori',
    requiredProvince: 'La província és obligatòria',
    invalidPhone: 'El telèfon no és correcte',
    product: 'producte',
    products: 'productes',
    openCart: 'Obrir cistella',
    minOrderError: 'La comanda mínima és de',
    deliveryZoneError: 'Ho sentim, no lliurem a la teva zona',
    geocodingError: 'No hem pogut verificar la teva adreça. Comprova que sigui correcta.',
    orderProcessError: 'Error en processar la comanda'
  },
  de: {
    requiredFirstName: 'Vorname ist erforderlich',
    requiredLastName: 'Nachname ist erforderlich',
    requiredAddress: 'Adresse ist erforderlich',
    requiredCity: 'Stadt ist erforderlich',
    requiredPostalCode: 'Postleitzahl ist erforderlich',
    requiredProvince: 'Bundesland ist erforderlich',
    invalidPhone: 'Telefonnummer ist ungültig',
    product: 'Produkt',
    products: 'Produkte',
    openCart: 'Warenkorb öffnen',
    minOrderError: 'Der Mindestbestellwert beträgt',
    deliveryZoneError: 'Leider liefern wir nicht in Ihre Region',
    geocodingError: 'Wir konnten Ihre Adresse nicht überprüfen. Bitte überprüfen Sie sie.',
    orderProcessError: 'Fehler bei der Bearbeitung der Bestellung'
  },
  eu: {
    requiredFirstName: 'Izena beharrezkoa da',
    requiredLastName: 'Abizena beharrezkoa da',
    requiredAddress: 'Helbidea beharrezkoa da',
    requiredCity: 'Hiria beharrezkoa da',
    requiredPostalCode: 'Posta-kodea beharrezkoa da',
    requiredProvince: 'Probintzia beharrezkoa da',
    invalidPhone: 'Telefonoa ez da zuzena',
    product: 'produktua',
    products: 'produktuak',
    openCart: 'Ireki saskia',
    minOrderError: 'Eskaera minimoa hau da:',
    deliveryZoneError: 'Sentitzen dugu, ez dugu zure eremuan banatzen',
    geocodingError: 'Ezin izan dugu zure helbidea egiaztatu. Egiaztatu zuzena dela.',
    orderProcessError: 'Errorea eskaera prozesatzean'
  }
}

const OrderingCart = ({ language, slug, barId, deliveryFee, minOrder, barCoords, currency }: Props) => {
  const { cart, removeFromCart, clearCart, updateQuantity } = useCart()
  const [isOpen, setIsOpen] = useState(false)
  const [whatToShow, setWhatToShow] = useState<'cartList' | 'shippingDetails' | 'payment'>('cartList')
  const [loading, setLoading] = useState(false)
  const [checkoutSecret, setCheckoutSecret] = useState<string>('')
  const [connectAccountId, setConnectAccountId] = useState<string>('')
  const tableId = 'delivery' // Cambiar por el parámetro de la URL si es para pedido en mesa

  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth)

  const [shippingDetails, setShippingDetails] = useState<IShippingDetails>({
    firstName: '',
    lastName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    postalCode: '',
    province: '',
    phone: '',
    additionalInfo: ''
  })

  const [shippingErrors, setShippingErrors] = useState<{ [key: string]: string }>({})

  const [errorMessage, setErrorMessage] = useState<string>('')
  const [openSnackbar, setOpenSnackbar] = useState(false)

  // Actualizar el ancho de la ventana al redimensionar
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Deshabilitar el scroll del body cuando el carrito está abierto
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'auto'
  }, [isOpen])

  // Calcular el total del carrito y la cantidad total de productos
  const total: number = cart.reduce(
    (acc, item) => acc + item.variant.price * item.quantity,
    0
  )
  const totalItems = cart.reduce((acc, item) => acc + item.quantity, 0)

  // Manejar el cambio de cantidad
  const handleQuantityChange = (
    itemId: string,
    variantType: string,
    quantity: number
  ) => {
    if (quantity < 1) return
    updateQuantity(itemId, variantType, quantity)
  }

  // Alternar la vista del carrito
  const toggleCart = () => {
    setIsOpen(!isOpen)
  }

  const validateShippingDetails = (): { [key: string]: string } => {
    const errors: { [key: string]: string } = {}
    const texts = translations[language] || translations.es

    if (!shippingDetails.firstName.trim()) errors.firstName = texts.requiredFirstName
    if (!shippingDetails.lastName.trim()) errors.lastName = texts.requiredLastName
    if (!shippingDetails.addressLine1.trim()) errors.addressLine1 = texts.requiredAddress
    if (!shippingDetails.city.trim()) errors.city = texts.requiredCity
    if (!shippingDetails.postalCode.trim()) errors.postalCode = texts.requiredPostalCode
    if (!shippingDetails.province.trim()) errors.province = texts.requiredProvince
    if (
      shippingDetails.phone.trim().length < 7 ||
      !/^\+?\d+$/.test(shippingDetails.phone.trim())
    ) { errors.phone = texts.invalidPhone }

    return errors
  }

  const handleSubmitOrder = async () => {
    if (cart.length === 0) return

    try {
      setLoading(true)
      const orderPayload: IOrderPayload = {
        slug,
        language,
        order: cart,
        tableId,
        barId,
        shippingDetails
      }
      const { data } = await axios.post('/api/ordering/checkout', orderPayload)

      // Show embedded Stripe checkout inside the cart
      setCheckoutSecret(data.clientSecret)
      setConnectAccountId(data.connectId)
      setWhatToShow('payment')
    } catch (error: any) {
      const texts = translations[language] || translations.es
      let errorMsg = texts.orderProcessError

      // Handle specific error messages
      const serverMessage = error.response?.data?.message
      if (serverMessage) {
        if (serverMessage.includes('MIN_ORDER_ERROR')) {
          errorMsg = `${texts.minOrderError} ${formatMoney(minOrder, currency)}`
        } else if (serverMessage.includes('DELIVERY_ZONE_ERROR')) {
          errorMsg = texts.deliveryZoneError
        } else if (serverMessage.includes('GEOCODING_ERROR')) {
          errorMsg = texts.geocodingError
        }
      }

      setErrorMessage(errorMsg)
      setOpenSnackbar(true)
    } finally {
      setLoading(false)
    }
  }

  const handleMainBtnClick = () => {
    if (whatToShow === 'cartList') {
      setWhatToShow('shippingDetails')
    } else if (whatToShow === 'shippingDetails') {
      const errors = validateShippingDetails()
      if (Object.keys(errors).length > 0) {
        setShippingErrors(errors)
        return
      }
      handleSubmitOrder()
    }
    // 'payment' view has its own submit button inside EmbeddedCheckout
  }

  const handleSecondaryBtnClick = () => {
    if (whatToShow === 'cartList') {
      clearCart()
      setIsOpen(false)
    } else if (whatToShow === 'shippingDetails') {
      setWhatToShow('cartList')
    } else if (whatToShow === 'payment') {
      setWhatToShow('shippingDetails')
    }
  }

  const texts = translations[language] || translations.es

  return (
    <>
      {/* Overlay solo se muestra cuando el carrito está abierto */}
      {isOpen && (
        <div
          className='orderingCart__overlay'
          onClick={toggleCart}
        />
      )}

      {/* Vista completa del carrito */}
      <div
        className={`orderingCart__container ${
          isOpen
            ? windowWidth >= 960
              ? 'orderingCart__container--open'
              : 'orderingCart__container--open'
            : windowWidth >= 960
            ? 'orderingCart__container--closed'
            : 'orderingCart__container--closed'
        }`}
      >
        {whatToShow === 'cartList'
          ? (
            <CartList
              toggleCart={toggleCart}
              cart={cart}
              removeFromCart={removeFromCart}
              handleQuantityChange={handleQuantityChange}
              language={language}
              currency={currency}
            />
            )
          : whatToShow === 'shippingDetails'
            ? (
              <ShippingDetails
                shippingDetails={shippingDetails}
                setShippingDetails={setShippingDetails}
                toggleCart={toggleCart}
                errors={shippingErrors}
                language={language}
                proximity={barCoords}
              />
              )
            : (
              <div className='orderingCart__embedded-payment'>
                <DeliveryEmbeddedCheckout
                  clientSecret={checkoutSecret}
                  connectId={connectAccountId}
                  onClose={() => setWhatToShow('shippingDetails')}
                  language={language}
                />
              </div>
              )}

        {/* Total y botones de acción fijados al fondo */}
        {whatToShow !== 'payment' && (
          <ActionButtons
            total={total}
            whatToShow={whatToShow as 'cartList' | 'shippingDetails'}
            handleSubmitClick={handleMainBtnClick}
            handleSecondaryBtnClick={handleSecondaryBtnClick}
            nItems={totalItems}
            deliveryFee={deliveryFee}
            minOrder={minOrder}
            loading={loading}
            language={language}
            currency={currency}
          />
        )}
      </div>

      {/* Barra minimizada del carrito */}
      {!isOpen && (
        <div
          className='orderingCart__minimized'
          onClick={toggleCart}
        >
          {/* Icono de cesta */}
          <div className='orderingCart__minimized__icon'>
            <ShoppingCartIcon size={20} />
          </div>

          {/* Información de productos */}
          <div className='orderingCart__minimized__info'>
            <span>
              {totalItems} {totalItems === 1 ? texts.product : texts.products}
            </span>
          </div>

          {/* Precio */}
          <div className='orderingCart__minimized__price'>
            <span>{formatMoney(total, currency)}</span>
          </div>
        </div>
      )}

      <AppSnackbar
        open={openSnackbar}
        onClose={() => setOpenSnackbar(false)}
        severity='error'
        title={errorMessage}
      />
    </>
  )
}

export default OrderingCart
